// src/pages/MySkills/useMySkills.js
import { useEffect, useState } from "react";

import {
  fetchReflectionTexts,
  callSkillAnalysis,
  fetchPeriodSkillAnalysis,
  fetchTodaySkill
} from "../../api/my_skills_api";

export default function useMySkills() {
  const email = localStorage.getItem("email");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [analysis, setAnalysis] = useState(null);
  const [periodAnalysis, setPeriodAnalysis] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const [period, setPeriod] = useState("all");
  const [selectedDate, setSelectedDate] = useState(() => {
    const t = new Date();
    return t.toISOString().split("T")[0];
  });

  const [todaySkills, setTodaySkills] = useState([]);

  /* Load All-Time Analysis */
  const loadFullAnalysis = async () => {
    setLoading(true);
    setError("");
    setAnalysis(null);

    try {
      if (!email) throw new Error("Login required.");

      const texts = await fetchReflectionTexts(email);

      if (!texts.length) throw new Error("No reflections found.");

      const data = await callSkillAnalysis(texts);

      setAnalysis(data);
      setLastUpdated(new Date().toISOString());
    } catch (err) {
      setError(err.message);
    }

    setLoading(false);
  };

  /* Load Period-Specific Analysis */
  const loadPeriodAnalysis = async () => {
    setLoading(true);
    setError("");
    setPeriodAnalysis(null);

    try {
      if (period === "all") {
        await loadFullAnalysis();
        await loadTodaySkill();
        return;
      }

      if (!selectedDate) throw new Error("Select a valid date.");

      const data = await fetchPeriodSkillAnalysis(email, period, selectedDate);

      setPeriodAnalysis(data);
      setLastUpdated(new Date().toISOString());
    } catch (err) {
      setError(err.message);
    }

    setLoading(false);
  };

  /* Load Today Skills */
  const loadTodaySkill = async () => {
    try {
      const skills = await fetchTodaySkill(email);
      setTodaySkills(skills || []);
    } catch {
      setTodaySkills([]);
    }
  };

  /* Initial Load */
  useEffect(() => {
    loadFullAnalysis();
    loadTodaySkill();
  }, []);

  /* Utils */
  const levelLabel = (n) => {
    if (!n) return "Beginner";
    if (n >= 5) return "Expert";
    if (n >= 4) return "Advanced";
    if (n >= 2) return "Intermediate";
    return "Beginner";
  };

  const refreshAll = () => {
    if (period === "all") {
      loadFullAnalysis();
    } else {
      loadPeriodAnalysis();
    }
    loadTodaySkill();
  };

  return {
    loading,
    error,
    analysis,
    periodAnalysis,
    lastUpdated,
    period,
    setPeriod,
    selectedDate,
    setSelectedDate,
    todaySkills,
    levelLabel,
    refreshAll,
    loadPeriodAnalysis,
    loadFullAnalysis,
  };
}
